import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { loggers } from '@/utils/logger'

const log = loggers.uiStore
const logStateChange = loggers.stateChange

export interface SelectedComponent {
  id: number
  type: string
  name: string
  data: any
}

// Structure component selection (for layout containers like FlexSection, LayoutGrid)
export interface SelectedStructure {
  id: number
  type: string
  name: string
  data: any
}

// Pending changes for a component
export interface PendingComponentChanges {
  [property: string]: any
}

// Container component types that should use StructureWrapper
export const CONTAINER_COMPONENT_TYPES = [
  'cms.flex-section',
  'cms.layout-grid'
] as const

export type ContainerComponentType = typeof CONTAINER_COMPONENT_TYPES[number]

// Helper to check if a component type is a container
export const isContainerComponent = (type: string): boolean => {
  return CONTAINER_COMPONENT_TYPES.includes(type as ContainerComponentType)
}

export const useUIStore = defineStore('ui', () => {
  // State - selection state kept in store as requested
  const selectedComponent = ref<SelectedComponent | null>(null)  // Content component selection (blue border)
  const selectedStructure = ref<SelectedStructure | null>(null)  // Structure component selection (purple border)
  const hoveredComponent = ref<number | null>(null)
  const hoveredStructure = ref<number | null>(null)
  const showGrid = ref(false)
  const showComponentOutlines = ref(true)
  const showComponentLibrary = ref(false)
  const insertionPoint = ref<{ position: number; parentId?: number } | null>(null)

  // Pending changes - keyed by component ID
  const pendingChanges = ref<Map<number, PendingComponentChanges>>(new Map())

  // Getters
  const hasSelection = computed(() => selectedComponent.value !== null)
  const hasStructureSelection = computed(() => selectedStructure.value !== null)
  const hasAnySelection = computed(() => selectedComponent.value !== null || selectedStructure.value !== null)
  const hasUnsavedChanges = computed(() => pendingChanges.value.size > 0)

  // Actions - simplified selection management
  const selectComponent = (component: SelectedComponent | null): void => {
    selectedComponent.value = component
    log('Component selected:', component?.id)
  }

  const hoverComponent = (componentId: number | null): void => {
    hoveredComponent.value = componentId
  }

  const clearSelection = (): void => {
    selectedComponent.value = null
    log('Selection cleared')
  }

  // Structure selection actions - for container components (FlexSection, LayoutGrid)
  const selectStructure = (structure: SelectedStructure | null): void => {
    selectedStructure.value = structure
    log('Structure selected:', structure?.id)
  }

  const hoverStructure = (structureId: number | null): void => {
    hoveredStructure.value = structureId
  }

  const clearStructureSelection = (): void => {
    selectedStructure.value = null
    log('Structure selection cleared')
  }

  // Clear both selections (for clicking outside)
  const clearAllSelections = (): void => {
    selectedComponent.value = null
    selectedStructure.value = null
    log('All selections cleared')
  }



  const toggleGrid = (): void => {
    showGrid.value = !showGrid.value
  }

  const toggleComponentOutlines = (): void => {
    showComponentOutlines.value = !showComponentOutlines.value
  }

  const showComponentLibraryAction = (): void => {
    log('showComponentLibrary called')
    showComponentLibrary.value = true
  }

  const hideComponentLibraryAction = (): void => {
    log('hideComponentLibrary called')
    showComponentLibrary.value = false
    insertionPoint.value = null
  }

  const setInsertionPoint = (position: number, parentId?: number): void => {
    log('setInsertionPoint called', { position, parentId })
    insertionPoint.value = { position, parentId }
  }

  // Pending changes management
  const updateComponentProperty = (componentId: number, property: string, value: any): void => {
    const existing = pendingChanges.value.get(componentId) || {}
    const newMap = new Map(pendingChanges.value)
    newMap.set(componentId, { ...existing, [property]: value })
    pendingChanges.value = newMap

    // Also update the selected component's data for immediate UI feedback
    if (selectedComponent.value?.id === componentId) {
      selectedComponent.value = {
        ...selectedComponent.value,
        data: { ...selectedComponent.value.data, [property]: value }
      }
    }

    log('Property changed locally', { componentId, property })
  }

  const getPendingChanges = (): Map<number, PendingComponentChanges> => {
    return pendingChanges.value
  }

  const clearPendingChanges = (): void => {
    pendingChanges.value = new Map()
    log('Pending changes cleared')
  }

  const clearPendingChangesForComponent = (componentId: number): void => {
    pendingChanges.value.delete(componentId)
  }

  // Component selection logic removed - now handled locally in AdminComponentWrapper

  // Keyboard handlers removed - now handled in AdminComponentWrapper


  // Debug tools for browser console - simplified
  const debugTools = {
    logState: () => {
      console.group('🎯 UI Store Current State')
      console.log('Selected Component:', selectedComponent.value)
      console.log('Selected Structure:', selectedStructure.value)
      console.log('Hovered Component:', hoveredComponent.value)
      console.log('Hovered Structure:', hoveredStructure.value)
      console.log('Show Grid:', showGrid.value)
      console.log('Show Outlines:', showComponentOutlines.value)
      console.log('Show Component Library:', showComponentLibrary.value)
      console.groupEnd()
    }
  }

  // Expose debug tools to global scope
  if (typeof window !== 'undefined') {
    (window as any).debugUI = debugTools
    log('🛠️  Debug tools attached to window.debugUI')
  }

  return {
    // State
    selectedComponent,
    selectedStructure,
    hoveredComponent,
    hoveredStructure,
    showGrid,
    showComponentOutlines,
    showComponentLibrary,
    insertionPoint,
    pendingChanges,

    // Getters
    hasSelection,
    hasStructureSelection,
    hasAnySelection,
    hasUnsavedChanges,

    // Actions - content components
    selectComponent,
    hoverComponent,
    clearSelection,

    // Actions - structure components
    selectStructure,
    hoverStructure,
    clearStructureSelection,
    clearAllSelections,

    // UI toggle actions
    toggleGrid,
    toggleComponentOutlines,

    // Component Library actions
    showComponentLibraryAction,
    hideComponentLibrary: hideComponentLibraryAction,
    setInsertionPoint,

    // Pending changes actions
    updateComponentProperty,
    getPendingChanges,
    clearPendingChanges,
    clearPendingChangesForComponent,

    // Debug tools
    debugTools
  }
})